import React, { useEffect, useMemo, useState } from "react";
import { collection, doc, onSnapshot, orderBy, query } from "firebase/firestore";
import { useLocation } from "react-router-dom";
import { database } from "../../database/firebase";
import "./leaderboard.css";
import { setBackButtonUrl } from '../uiSlice';
import { useSelector, useDispatch } from "react-redux";

function readStoredUserData() {
  try {
    const raw =
      localStorage.getItem("userData") ||
      sessionStorage.getItem("userData") ||
      "{}";
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
}

function normalizeCharacterName(character) {
  const value = String(character || "").trim().toLowerCase();
  return /^c[1-6]$/.test(value) ? value : "";
}

function getDisplayName(userRow) {
  const name = String(userRow?.name || "").trim();
  if (name && name.toLowerCase() !== "null null") return name;
  const email = String(userRow?.email || "").trim();
  if (email) return email.split("@")[0] || "Player";
  return "Player";
}

function rankLabel(index) {
  if (index === 1) return "2ND";
  if (index === 2) return "3RD";
  if (index === 3) return "4TH";
  return `${index + 1}TH`;
}

export default function Leaderboard() {
  const location = useLocation();
  const userData = useMemo(() => readStoredUserData(), []);
  const themeData = useSelector((state) => state.theme?.data);
  const textStyle = themeData?.text_color ? { color: themeData.text_color } : undefined;
  const sessionId = String(
    userData?.sessionId || userData?.sessionID || userData?.session_id || ""
  ).trim();
  const lobbyName = String(
    userData?.lobby ||
      userData?.lobbyName ||
      userData?.lobby_name ||
      location.state?.lobbyName ||
      location.state?.lobby ||
      ""
  ).trim();

  const [usersMap, setUsersMap] = useState({});
  const [playerStatsMap, setPlayerStatsMap] = useState({});
  const [loadingUsers, setLoadingUsers] = useState(true);

  const { status, user, error } = useSelector((state) => state.auth);
  const dispatch = useDispatch();


  useEffect(() => {
    dispatch(setBackButtonUrl("jenga"));
  }, [status, user]);


  useEffect(() => {
    if (!sessionId || !lobbyName) {
      setLoadingUsers(false);
      return undefined;
    }

    const usersRef = collection(database, `lobbies/${sessionId}/lobby/${lobbyName}/users`);
    const unsub = onSnapshot(
      query(usersRef, orderBy("timestamp", "asc")),
      (snap) => {
        const next = {};
        snap.forEach((d) => {
          const row = d.data() || {};
          const uid = String(row?.userId || d.id || "").trim();
          if (!uid) return;
          next[uid] = row;
        });
        setUsersMap(next);
        setLoadingUsers(false);
      },
      () => setLoadingUsers(false)
    );

    return () => unsub();
  }, [lobbyName, sessionId]);

  useEffect(() => {
    if (!sessionId || !lobbyName) return undefined;

    const liveRef = doc(database, `lobbies/${sessionId}/lobby/${lobbyName}/state/gameover`);
    const unsub = onSnapshot(liveRef, (snap) => {
      const data = snap.data() || {};
      const stats =
        data?.playerStats && typeof data.playerStats === "object"
          ? data.playerStats
          : {};
      setPlayerStatsMap(stats);
    });

    return () => unsub();
  }, [lobbyName, sessionId]);

  const ranked = useMemo(() => {
    const knownIds = new Set([
      ...Object.keys(usersMap || {}),
      ...Object.keys(playerStatsMap || {}),
    ]);
    const rows = Array.from(knownIds).map((uid) => {
      const u = usersMap?.[uid] || {};
      const s = playerStatsMap?.[uid] || {};
      const score = Number.isFinite(Number(s?.score))
        ? Number(s.score)
        : Number(u?.score || 0);
      const wrongAnswers = Number.isFinite(Number(s?.wrongAnswers))
        ? Number(s.wrongAnswers)
        : Number(u?.wrongAnswers || 0);
      return {
        userId: uid,
        score,
        wrongAnswers,
        name: getDisplayName({ ...u, ...s }),
        character: normalizeCharacterName(s?.character) || normalizeCharacterName(u?.character),
      };
    });

    return rows.sort((a, b) => {
      // Highest score is always on top.
      if (b.score !== a.score) return b.score - a.score;
      if (a.wrongAnswers !== b.wrongAnswers) return a.wrongAnswers - b.wrongAnswers;
      return a.name.localeCompare(b.name);
    });
  }, [playerStatsMap, usersMap]);

  const winner = ranked[0];
  const others = ranked.slice(1);

  return (
    <div className="bnb-leaderboard-page">

      {!sessionId || !lobbyName ? (
        <div className="bnb-empty-state" style={textStyle}>Missing lobby/session details.</div>
      ) : loadingUsers && ranked.length === 0 ? (
        <div className="bnb-empty-state" style={textStyle}>Loading leaderboard...</div>
      ) : ranked.length === 0 ? (
        <div className="bnb-empty-state" style={textStyle}>No leaderboard data yet.</div>
      ) : (
        <div className="bnb-podium">
          <div className="bnb-winner-title" style={textStyle}>WINNER</div>

          <div className="bnb-winner-card">
            <div className="bnb-avatar-frame bnb-winner-avatar">
              {winner?.character ? (
                <img src={`/img/${winner.character}.png`} alt={winner.name} />
              ) : (
                <span>🙂</span>
              )}
            </div>
            <div className="bnb-player-name" style={textStyle}>{winner?.name || "Player"}</div>
            <div className="bnb-winner-score" style={textStyle}>SCORE {winner?.score ?? 0}</div>
          </div>

          <div
            className={`bnb-runners-row ${
              others.length === 1
                ? "bnb-runners-row--single"
                : others.length === 2
                ? "bnb-runners-row--double"
                : "bnb-runners-row--multi"
            }`}
          >
            {others.map((player, idx) => (
              <div key={player.userId || `${player.name}-${idx}`} className="bnb-runner-col">
                <div className={`bnb-runner-card bnb-runner-card-${idx + 2}`}>
                  <div
                    className="bnb-avatar-frame bnb-runner-avatar"
                  >
                    {player?.character ? (
                      <img src={`/img/${player.character}.png`} alt={player.name} />
                    ) : (
                      <span>🙂</span>
                    )}
                  </div>
                  <div className="bnb-runner-name" style={textStyle}>{player?.name || "Player"}</div>
                  <div className="bnb-runner-score" style={textStyle}>SCORE {player?.score ?? 0}</div>
                </div>
                <div className="bnb-rank-label" style={textStyle}>{rankLabel(idx + 1)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}